/**
 * SharedCollectionHeader Component
 * Header for collection views with animated sock icon and font size control
 * Used in both user collection view and guest collection view
 */

import React from 'react';
import { useFontSize, FontSize } from '../hooks/useFontSize';
import '../styles/components/sharedCollectionHeader.css';

interface SharedCollectionHeaderProps {
  ownerName?: string; // Owner name to display "<name> sharing" in collection info
  ownerEmail?: string; // Owner email for mailto link
  collectionName: string;
  collectionDescription?: string;
  itemCount: number;
  permission?: 'view' | 'edit' | 'full_edit'; // User's permission level
  isOwner?: boolean; // Whether current user is the collection owner
  onSockClick?: () => void; // Optional callback for sock icon click (e.g., voice search)
  isVoiceListening?: boolean; // Whether voice search is active
  onGuestProfileClick?: () => void; // Optional callback for guest profile button (guest view only)
  guestName?: string; // Guest display name (for tooltip)
}

const SharedCollectionHeader: React.FC<SharedCollectionHeaderProps> = ({
  ownerName,
  ownerEmail,
  collectionName,
  collectionDescription,
  itemCount,
  permission,
  isOwner,
  onSockClick,
  isVoiceListening = false,
  onGuestProfileClick,
  guestName
}) => {
  const { fontSize, setFontSize } = useFontSize();
  
  // Font size cycling function
  const cycleFontSize = () => {
    const sizes: FontSize[] = ['small', 'medium', 'large', 'x-large'];
    const currentIndex = sizes.indexOf(fontSize);
    const nextIndex = (currentIndex + 1) % sizes.length;
    setFontSize(sizes[nextIndex]);
  };
  
  return (
    <div className="shared-collection-header">
      {/* Top row with guest profile button (if guest view) */}
      {onGuestProfileClick && (
        <div className="shared-header-top-actions">
          <button
            onClick={onGuestProfileClick}
            className="guest-profile-btn"
            title={guestName ? `Signed in as ${guestName}` : 'Edit profile'}
            aria-label="Edit guest profile"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </button>
        </div>
      )}
      
      {/* Shared by info only - collection name now shown in UnifiedHeader */}
      <div className="shared-collection-info">
        {!isOwner && ownerName && (
          <p className="shared-owner-text">
            shared by {ownerEmail ? (
              <a href={`mailto:${ownerEmail}`} className="owner-email-link">
                {ownerName}
              </a>
            ) : (
              ownerName
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default SharedCollectionHeader;
